
<template>
  <div class="device-info" ref="scrollContainer">
    <h2>
      Info
      <span class="env-badge" :class="environment.toLowerCase()">{{ environment }}</span>
    </h2>

    <!-- Farbtasten-Legende. Steht oben, wo die Seite beginnt und wo man beim
         Betreten hinsieht - und noch einmal als feste Leiste am unteren Rand,
         weil die Seite lang ist und die obere Legende nach dem ersten Scrollen
         weg ist. Beide kommen aus keyLegend, damit sie nicht auseinanderlaufen. -->
    <div class="keybar keybar--top">
      <span class="keybar-title">Farbtasten</span>
      <span class="keybar-item" v-for="entry in keyLegend" :key="entry.label">
        <i class="dot" :class="entry.dot" v-if="entry.dot"></i>{{ entry.label }}
      </span>
    </div>
    <template v-if="loading">
      <!--<spinner></spinner>-->
      Loading...
    </template>
    <template v-else>
      <!-- Notation: Live-Wert vom Gerät, dahinter in Klammern der abweichende
           DB-Wert. Steht keine Klammer, sind beide gleich (oder es gibt für den
           Schlüssel keinen Live-Wert). -->
      <div class="legend">
        Angezeigt: <strong>Live-Wert vom Gerät</strong><span class="current"> (DB-Wert, falls abweichend)</span><span
          class="missing"
        > (DB: leer)</span>
        <span class="count" v-if="staleCount"> — {{ staleCount }} abweichend</span>
        <span class="missing" v-if="missingCount"> — {{ missingCount }} in der DB leer</span>
        <br />
        <span class="readable">DB-Stand: {{ dbFromApi ? 'direkt aus der API gelesen' : 'aus dem Store (API nicht erreichbar)' }}</span>
      </div>

      <div class="info-columns">
        <div class="info-col" v-for="(col, ci) in columns" :key="ci">
          <table>
            <tr class="info-item" v-for="row in col" :key="row.key">
              <td class="key">{{ row.key }}</td>
              <td class="value">
                {{ formatValue(row.value) }}<span
                  v-if="row.current !== undefined"
                  class="current"
                > ({{ formatValue(row.current) }})</span><span
                  v-else-if="row.dbMissing"
                  class="missing"
                > (DB: leer)</span><span
                  v-if="row.readable"
                  class="readable"
                > ({{ row.readable }})</span>
              </td>
            </tr>
          </table>
        </div>
      </div>

      <!-- Versionserkennung: bewusst mit allen Rohwerten, damit eine falsche
           Zuordnung (interne Plattformversion -> webOS-Generation) auf echter
           Hardware sofort sichtbar ist. -->
      <div class="diagnostics" v-if="webosRows.length">
        <h3>WebOS-Version (Quelle: {{ webosSource }})</h3>
        <table>
          <tr class="info-item" v-for="row in webosRows" :key="row.key">
            <td class="key">{{ row.key }}</td>
            <td class="value">{{ row.value }}</td>
          </tr>
        </table>
      </div>

      <div class="diagnostics" v-if="castRows.length">
        <h3>Cast-Diagnose</h3>
        <table>
          <tr class="info-item" v-for="row in castRows" :key="row.key">
            <td class="key">{{ row.key }}</td>
            <td class="value">{{ row.value }}</td>
          </tr>
        </table>
      </div>

      <!-- Was jede lesende SoftAP-Funktion des Geräts antwortet. Samsung
           dokumentiert die TEP-Netzwerk-APIs nicht, und die Namen wechseln je
           Firmware - das hier ist der einzige Weg, sie zu erfahren, statt am
           Gerät zu raten. Interessant ist die Zeile, deren Wert sich ändert,
           wenn sich ein Smartphone verbindet: das ist die Client-Abfrage. -->
      <div class="diagnostics" v-if="softApProbeRows.length">
        <h3>SoftAP-Funktionen am Gerät ({{ softApProbeRows.length }})</h3>
        <table>
          <tr class="info-item" v-for="row in softApProbeRows" :key="row.key">
            <td class="key">{{ row.key }}</td>
            <td class="value">{{ row.value }}</td>
          </tr>
        </table>
      </div>

      <div class="diagnostics" v-if="castPropertyRows.length">
        <h3>Cast-Properties am Gerät ({{ castPropertyKnown }} von {{ castPropertyRows.length }} bekannt)</h3>
        <table>
          <tr class="info-item" v-for="row in castPropertyRows" :key="row.key">
            <td class="key">{{ row.key }}</td>
            <td class="value">{{ row.value }}</td>
          </tr>
        </table>
      </div>

      <!-- Geräteinterne Einstellungen: Service-/Hotel-Menü (Samsung) bzw.
           Pro:Centric-/Installer-Menü (LG). Dieselben Zeilen, die auch der
           SocketAdmin bekommt und dort setzen kann - sie kommen aus
           Diagnostics.settingsRows(), damit Bildschirm und Ferndiagnose
           dieselben Werte zeigen. In Klammern steht, über welchen nativen
           Weg der Wert kam; das ist der Beleg, welche Menü-Id bzw. Property
           dieses Modell wirklich kennt. -->
      <div class="diagnostics" v-if="deviceSettingRows.length">
        <h3>
          Geräteinterne Einstellungen ({{ deviceSettingsSupported }} von
          {{ deviceSettingsTotal }} unterstützt)
        </h3>
        <table>
          <tr class="info-item" v-for="row in deviceSettingRows" :key="row.key">
            <td class="key">{{ row.key }}</td>
            <td class="value">{{ row.value }}</td>
          </tr>
        </table>
      </div>

      <!-- Speicher und Dateien: liegt ein Clonefile an einer Stelle, an die
           die App herankommt? Erzeugen kann sie keins - das geht nur ueber das
           Hotelmenü auf einen USB-Stick. -->
      <div class="diagnostics" v-if="storageProbeRows.length">
        <h3>Speicher und Dateien am Gerät</h3>
        <table>
          <tr class="info-item" v-for="row in storageProbeRows" :key="row.key">
            <td class="key">{{ row.key }}</td>
            <td class="value">{{ row.value }}</td>
          </tr>
        </table>
      </div>

      <!-- Was das Gerät an Einstellungs-APIs mitbringt. Die Kandidaten im
           Katalog sind zum Teil geraten - diese Liste sagt, was wirklich da
           ist, inklusive der Namen, auf die niemand gekommen wäre. -->
      <div class="diagnostics" v-if="deviceSettingApiRows.length">
        <h3>Einstellungs-API des Geräts</h3>
        <table>
          <tr class="info-item" v-for="row in deviceSettingApiRows" :key="row.key">
            <td class="key">{{ row.key }}</td>
            <td class="value">{{ row.value }}</td>
          </tr>
        </table>
      </div>

      <!-- Was in den vier Speichern des Geraets steht. Dieselben Zeilen, die
           auch die get_info-Antwort an den Socket-Admin traegt - sie kommen
           aus Diagnostics.storageSections(), damit Bildschirm und Ferndiagnose
           nicht auseinanderlaufen. -->
      <div class="diagnostics" v-for="section in storageSections" :key="section.title">
        <h3>{{ section.title }}</h3>
        <table>
          <tr class="info-item" v-for="row in section.rows" :key="row.key">
            <td class="key">{{ row.key }}</td>
            <td class="value">{{ row.value }}</td>
          </tr>
        </table>
      </div>

      <!-- Vorinstallierte Apps mit NUMERISCHER Id. Premium-OTT liegt hier und
           die Ids sind pro Gerät/Region unterschiedlich - nur so lassen sie
           sich für DEFAULT_CAST_APPS ermitteln. -->
      <div class="diagnostics" v-if="preloadedAppRows.length">
        <h3>Preloaded-Apps ({{ preloadedAppRows.length }})</h3>
        <table>
          <tr class="info-item" v-for="row in preloadedAppRows" :key="row.key">
            <td class="key">{{ row.key }}</td>
            <td class="value">{{ row.value }}</td>
          </tr>
        </table>
      </div>

      <div class="installed-apps" v-if="installedApps.length">
        <h3>Cast-relevante Apps ({{ castApps.length }} von {{ installedApps.length }} im Katalog)</h3>
        <table>
          <tr class="info-item" v-for="app in castApps" :key="app.id">
            <td class="key">{{ app.label }}</td>
            <td class="value">
              {{ app.id }}<span v-if="app.version" class="current"> — v{{ app.version }}</span><span
                v-if="!app.installed"
                class="current"
              > — nicht installiert (valid=false)</span>
            </td>
          </tr>
          <tr v-if="!castApps.length">
            <td class="value" colspan="2">Keine Cast-relevanten Apps gefunden</td>
          </tr>
        </table>
      </div>
    </template>

    <!-- Farbtasten-Navigation der Service-Seiten.
         Aus dem Hauptmenü führt ROT-GRÜN-GELB-BLAU-ROT hierher; von hier aus
         genügt eine einzelne Farbtaste. Diese Leiste ist fest positioniert und
         bleibt beim Scrollen stehen. -->
    <div class="keybar">
      <span class="keybar-item" v-for="entry in keyLegend" :key="entry.label">
        <i class="dot" :class="entry.dot" v-if="entry.dot"></i>{{ entry.label }}
      </span>
    </div>

    <!-- Sicherheitsabfrage zum Speicher-Löschen. Sie MUSS sein: danach sind
         device_id, bid, room und fingerprint weg und das Gerät muss neu
         registriert werden. Vorbelegt ist deshalb "Abbrechen". -->
    <div class="dialog" v-if="dialog">
      <div class="dialog-box">
        <div class="dialog-title">{{ dialogTitle }}</div>
        <div class="dialog-text">{{ dialogText }}</div>
        <div class="dialog-report" v-if="clearReport">{{ clearReport }}</div>
        <div class="dialog-buttons">
          <div
            class="dialog-button"
            :class="{ 'dialog-button--active': index === dialogIndex }"
            v-for="(button, index) in dialogButtons"
            :key="button.action"
          >
            {{ button.label }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { keyHandler, KEYS } from '@/mixins/keyHandler';
//import router from '@/router';
import { NAV_TYPE } from '@/store/modules/navigation';
import DeviceType from '@/helpers/DeviceType';
import Device from '../helpers/Device';
import Requests from '@/helpers/Request';
import Registration from '@/helpers/Registration';
import Diagnostics from '@/helpers/Diagnostics';
import DeviceSettings from '@/helpers/DeviceSettings';
import StorageProbe from '@/helpers/StorageProbe';
import StorageInspector from '@/helpers/StorageHandler/Inspector';
import { DEVICE_SETTINGS } from '@/helpers/DeviceSettings/catalog';

export default {
  mixins: [keyHandler],
  data() {
    return {
      deviceInfo: {},
      liveValues: {},
      // DB-Stand des Geraets. Nur fuer den Klammer-Vergleich - angezeigt wird
      // primaer immer der Live-Wert vom Geraet.
      dbValues: {},
      // true, wenn dbValues direkt aus der API stammt (statt aus dem Store).
      dbFromApi: false,
      appId: '',
      packageId: '',
      installedApps: [],
      loading: false,
      rows: [],
      // Ergebnis von Cast.getDiagnostics() (Strategie, Capabilities, Rohwerte
      // der Versionserkennung). null solange die Erkennung nicht durch ist.
      castDiag: null,
      // Schnappschuss der geraeteinternen Einstellungen (Service-/Pro:Centric-
      // Menue). null solange nicht gelesen - das Lesen ist asynchron und geht
      // auf LG ueber Dutzende Properties.
      deviceSettings: null,
      // Speicher/Dateien des Geraets. null solange nicht gelesen.
      storageProbe: null,
      // Offene Sicherheitsabfrage. '' = keine.
      //   'clear' -> "Speicher wirklich loeschen?"
      //   'done'  -> geloescht, Bericht steht daneben
      dialog: '',
      dialogIndex: 0,
      // Bericht von StorageInspector.clearAll(), als lesbare Zeile.
      clearReport: '',
    };
  },
  created() {
    this.getDataFromDevice()
  },
  methods: {
    test(event) {
      event.currentTarget.focus();
      console.log(event.currentTarget);
    },
    hanleRemote(event) {
      console.log('HANDLE REMOTE', event);
    },
    // App-Installation (/info-apps). Vorher lag sie hinter der Tastenfolge
    // ROT-GRUEN-GELB-BLAU-GELB; auf der Info-Seite genuegt jetzt GELB - wer
    // hier ist, hat die Folge im Hauptmenue schon getippt.
    openAppInstall() {
      console.log('[Info] GELB -> App-Installation');
      this.$router.push({ name: 'AppInstall' }).catch(() => {});
    },
    // BLAU: alle Speicher leeren - aber erst nach Rueckfrage.
    askClearStorage() {
      this.clearReport = '';
      this.dialogIndex = 0;
      this.dialog = 'clear';
    },
    closeDialog() {
      this.dialog = '';
      this.dialogIndex = 0;
    },
    // Tasten, solange die Abfrage steht. Sie faengt ALLES ab, damit hinter dem
    // Kasten nicht gescrollt oder navigiert wird.
    dialogKey(key) {
      const buttons = this.dialogButtons;
      switch (key) {
        case KEYS.LEFT:
          this.dialogIndex = this.dialogIndex > 0 ? this.dialogIndex - 1 : buttons.length - 1;
          return;
        case KEYS.RIGHT:
          this.dialogIndex = this.dialogIndex + 1 < buttons.length ? this.dialogIndex + 1 : 0;
          return;
        case KEYS.BACK:
        case KEYS.EXIT:
          this.closeDialog();
          return;
        case KEYS.ENTER:
          if (buttons[this.dialogIndex] && buttons[this.dialogIndex].action === 'clear') {
            this.clearStorage();
            return;
          }
          this.closeDialog();
          return;
        default:
          return;
      }
    },
    // Cookies, localStorage, sessionStorage und die bekannten Schluessel im
    // internen Speicher (Samsung keymanager) - dieselbe Stelle, die auch das
    // Kommando clear_storage des SocketAdmin benutzt.
    //
    // Die App laedt sich danach bewusst NICHT selbst neu: sie laeuft ohne
    // device_id weiter, und ein Geraet, das sich im selben Moment von allein
    // neu startet, ist am Bildschirm nicht von einem Absturz zu unterscheiden.
    // Der Bericht bleibt stehen, damit ablesbar ist, was wirklich weg ist.
    clearStorage() {
      let report = null;
      try {
        report = StorageInspector.clearAll();
      } catch (err) {
        this.clearReport = 'Fehlgeschlagen: ' + String((err && err.message) || err);
        this.dialogIndex = 0;
        this.dialog = 'done';
        return;
      }
      console.log('[Info] Speicher geleert:', JSON.stringify(report));
      const parts = [
        'Cookies: ' + report.cookie,
        'localStorage: ' + report.local,
        'sessionStorage: ' + report.session,
        'interner Speicher: ' + report.internal.length,
      ];
      if (report.errors && report.errors.length) parts.push('Fehler: ' + report.errors.join(', '));
      this.clearReport = parts.join(' \u00b7 ');
      this.dialogIndex = 0;
      this.dialog = 'done';
    },
    eventHandler(key) {
      // Steht die Sicherheitsabfrage, gehoert ihr jede Taste.
      if (this.dialog) {
        this.dialogKey(key);
        return;
      }
      switch (key) {
        case KEYS.UP:
          this.scrollBy(-1);
          break;
        case KEYS.DOWN:
          this.scrollBy(1);
          break;
        case KEYS.CHANNEL_UP:
        case KEYS.PAGE_UP:
          this.scrollBy(-1, true);
          break;
        case KEYS.CHANNEL_DOWN:
        case KEYS.PAGE_DOWN:
          this.scrollBy(1, true);
          break;
        case KEYS.RED:
          // Die Geraetedaten in der DB auf den Live-Stand bringen. Lag vorher
          // auf GELB - GELB navigiert jetzt weiter zur App-Installation.
          this.pushDeviceUpdateToApi();
          break;
        case KEYS.GREEN:
          // Versions-/Cast-Erkennung neu durchlaufen (nach Firmware-Update,
          // Netzwechsel oder Property-Aenderung ueber den Pro:Centric-Server).
          this.refreshDiagnostics(true);
          break;
        case KEYS.YELLOW:
          this.openAppInstall();
          break;
        case KEYS.BLUE:
          this.askClearStorage();
          break;
        case KEYS.BACK:
        case KEYS.ENTER:
        case KEYS.EXIT:
          this.$store.dispatch('navigation/to', NAV_TYPE.BACK);
          break;
      }
    },
    // Scrollt die (evtl. zu lange) Info-Seite per Fernbedienung.
    // dir: -1 hoch / +1 runter. page=true -> fast eine ganze Seite, sonst
    // ein fester Schritt. Kein smooth-scroll, damit es auch auf alten TVs
    // (Orsay/WebOS 3.5) zuverlaessig funktioniert.
    scrollBy(dir, page = false) {
      const el = this.$refs.scrollContainer;
      if (!el) return;
      const step = page ? Math.round(el.clientHeight * 0.85) : 120;
      el.scrollTop += dir * step;
    },
    async pushDeviceUpdateToApi() {
      const deviceId = this.$store.state.device.deviceId;
      const bid = this.$store.state.device.buildingId;
      if (!deviceId || !bid) {
        console.warn('[Info] pushDeviceUpdateToApi: deviceId oder buildingId fehlt');
        return;
      }
      try {
        const [mac, ip, serialNumber, fingerprint, model, version, firmware, platform] = await Promise.all([
          Device.mac,
          Device.ip,
          Device.serialNumber,
          Registration.getFingerprint(),
          Device.model,
          Device.version,
          Device.firmware,
          Device.platform,
        ]);
        const data = {
          mac,
          ip,
          serialNumber,
          fingerprint,
          resWidth: Device.resolutionWidth,
          resHeight: Device.resolutionHeight,
          type: DeviceType.getDeviceType,
          agent: DeviceType.getUserAgent,
          model,
          version,
          software: firmware,
          platform,
          updated: Math.floor(new Date().getTime() / 1000),
        };
        console.log('[Info] Device-Update senden:', data);
        await Requests.updateDeviceInfo({ bid, deviceId, data });
      } catch (err) {
        console.error('[Info] Device-Update fehlgeschlagen:', err);
      }
    },
    formatValue(value) {
      if (value === null || value === undefined) return '';
      if (typeof value === 'string') return value;
      return JSON.stringify(value);
    },
    // Versions- und Cast-Erkennung (neu) durchlaufen und Ergebnis uebernehmen.
    // Auf Nicht-LG-Geraeten liefert refineWebOSVersion() sofort null.
    async refreshDiagnostics(force = false) {
      try {
        if (DeviceType.isLG) await DeviceType.refineWebOSVersion(force);
        await this.$store.dispatch('cast/detect', force);
        this.castDiag = this.$store.getters['cast/getDiagnostics'];
        console.log('[Info] Cast-Diagnose:', this.castDiag);
      } catch (err) {
        console.log('[Info] Diagnose fehlgeschlagen:', err);
      }
    },
    // Die geraeteinternen Einstellungen neu am Geraet lesen.
    //
    // Fehlschlaege bleiben still: die Seite zeigt dann die letzte Fassung bzw.
    // gar keine Tabelle. Ein roter Kasten auf der Info-Seite waere hier
    // irrefuehrend - dass eine Firmware diese Einstellungen nicht herausgibt,
    // ist der Normalfall und steht Zeile fuer Zeile in der Tabelle.
    async refreshDeviceSettings() {
      try {
        this.deviceSettings = await DeviceSettings.read();
        console.log('[Info] Geraeteinterne Einstellungen:', this.deviceSettings);
      } catch (err) {
        console.log('[Info] Einstellungen nicht lesbar:', err);
      }
    },
    // Formatiert einen Wert fuer die Diagnose-Tabellen. Leere Werte werden zu
    // "—", damit "Property existiert nicht" von "Wert ist 0" unterscheidbar ist.
    diagValue(value) {
      return Diagnostics.value(value);
    },
    // Liefert ein lesbares Datum (DD.MM.YYYY HH:mm:ss), wenn der Wert ein
    // Unix-Timestamp (Sekunden/Millisekunden) oder ein ISO-Datum ist - sonst null.
    formatDate(key, value) {
      return Diagnostics.formatDate(key, value);
    },
    async getDataFromDevice() {
        this.loading = true;

        // Aktuelle (Live-)Werte direkt vom Geraet abfragen. Diese werden mit den
        // (evtl. veralteten) DB-Werten aus dem Store verglichen.
        const [mac, ip, serialNumber, model, version, firmware, platform] = await Promise.all([
          Device.mac,
          Device.ip,
          Device.serialNumber,
          Device.model,
          Device.version,
          Device.firmware,
          Device.platform,
        ]);
        const fingerprint = await Registration.getFingerprint();
        // Genau die Felder, die pushDeviceUpdateToApi() auch an die API meldet
        // (ohne 'updated'). So deckt der Klammer-Vergleich alles ab, was ROT
        // in der DB aktualisieren wuerde - type und agent gehoeren dazu.
        this.liveValues = {
          mac,
          ip,
          serialNumber,
          model,
          version,
          software: firmware,
          platform,
          fingerprint,
          resWidth: Device.resolutionWidth,
          resHeight: Device.resolutionHeight,
          type: DeviceType.getDeviceType,
          agent: DeviceType.getUserAgent,
        };

        const info = {};
        info.DeviceType = DeviceType.getDeviceType;

        const skipProps = new Set(['isDeviceLoaded', 'roomApps']);

        // Den DB-Stand direkt aus der API lesen, NICHT aus dem Store: device/init
        // baut den Store aus {...response, serialNumber, mac, ip} und ueberschreibt
        // die API-Werte dieser drei Felder mit Live-Werten. Der Store ist damit
        // kein getreues Abbild des Datensatzes - ein Vergleich gegen ihn kann fuer
        // diese Felder nie eine Abweichung zeigen.
        const bid = this.$store.state.device.buildingId;
        const deviceId = this.$store.state.device.deviceId;
        let dbRecord = null;
        if (bid && deviceId) {
          try {
            const res = await Requests.fetchDeviceInfo({ bid, deviceId });
            if (res && typeof res === 'object' && !Array.isArray(res)) dbRecord = res;
          } catch (err) {
            console.log('[Info] DB-Stand nicht abrufbar:', err);
          }
        }
        this.dbFromApi = !!dbRecord;

        const dbSource = dbRecord || this.$store.state.device;
        const dbValues = {};
        for(const prop in dbSource) {
          if(skipProps.has(prop)) continue;
          const val = dbSource[prop];
          if(val === null || val === undefined || val === '') continue;
          if(typeof val === 'function') continue;
          if(typeof val === 'object') continue;
          dbValues[prop] = val;
        }
        this.dbValues = dbValues;

        // Anzeige-Union: Store-Werte als Basis der Schluesselliste.
        for(const prop in this.$store.state.device) {
          if(skipProps.has(prop)) continue;
          const val = this.$store.state.device[prop];
          if(val === null || val === undefined || val === '') continue;
          if(typeof val === 'function') continue;
          if(typeof val === 'object') continue;
          info[prop] = val;
        }
        // Felder, die nur im DB-Datensatz stehen, ergaenzen.
        for(const prop in dbValues) {
          if(info[prop] === undefined) info[prop] = dbValues[prop];
        }

        // Live-only Geraetewerte ergaenzen, falls nicht aus DB vorhanden.
        for(const prop in this.liveValues) {
          if(info[prop] === undefined && this.liveValues[prop] !== undefined && this.liveValues[prop] !== '') {
            info[prop] = this.liveValues[prop];
          }
        }

        for(const prop in navigator) {
          if(navigator[prop]) {
            if(typeof navigator[prop] === 'function') continue;
            if(typeof navigator[prop] === 'object') continue;
            info[prop] = navigator[prop];
          }
        }
        this.deviceInfo = info;

        // Samsung AppID + installierte Apps.
        this.appId = Device.appId;
        this.packageId = Device.packageId;
        this.installedApps = await Device.getInstalledApps();

        // Versions-/Cast-Diagnose. Bewusst nach den Geraetedaten, damit die
        // Seite nicht auf die (u.U. langsamen) Property-Abfragen wartet.
        await this.refreshDiagnostics();

        // Und die geraeteinternen Einstellungen - aus demselben Grund zuletzt.
        await this.refreshDeviceSettings();
        // Ohne Lesetest: der hat auf einem U800F einen Neustart ausgeloest.
        // Wer ihn braucht, fordert ihn ueber das Kommando get_files an.
        try {
          this.storageProbe = await StorageProbe.read({ readTest: false });
        } catch (err) {
          console.log('[Info] Speicher nicht lesbar:', err);
        }

        console.log("===============deviceInfo", this.deviceInfo);
        this.loading = false;
        /*
        (async () => {
          console.log('---------------MAC   :', await Device.mac);
          console.log('---------------IP    :', await Device.ip);
          console.log('---------------SERIAL:', await Device.serialNumber);
          return Device.ip;
        })()
        .then(response => {
          this.loading = false
          this.rows = response
        })
        .catch(error => {
          this.loading = false
          console.log(error)
        })

        */
    },
  },
  computed: {
    environment() {
      return Diagnostics.environment();
    },
    // Tastenbelegung dieser Seite - Quelle fuer BEIDE Legenden (oben und die
    // feste Leiste unten). Zwei Aufzaehlungen im Markup wuerden bei der
    // naechsten Aenderung auseinanderlaufen.
    keyLegend() {
      return [
        { dot: 'dot-red', label: 'Gerätedaten an die API senden' },
        { dot: 'dot-green', label: 'Diagnose neu einlesen' },
        { dot: 'dot-yellow', label: 'App-Installation' },
        { dot: 'dot-blue', label: 'Speicher löschen' },
        // Ohne Punkt: keine Farbtaste, gehoert aber in die Legende.
        { dot: '', label: 'ZURÜCK: Hauptmenü' },
      ];
    },
    dialogTitle() {
      return this.dialog === 'done' ? 'Speicher geleert' : 'Speicher wirklich löschen?';
    },
    dialogText() {
      if (this.dialog === 'done') {
        return 'Das Gerät muss neu registriert werden. Neustart aus dem Hauptmenü: ROT-GRÜN-GELB-BLAU-GRÜN.';
      }
      return (
        'Cookies, localStorage, sessionStorage und der interne Speicher werden geleert. ' +
        'Damit sind device_id, bid, room und der Fingerprint weg - das Gerät muss danach neu registriert werden.'
      );
    },
    dialogButtons() {
      if (this.dialog === 'done') return [{ action: 'close', label: 'OK' }];
      // Die harmlose Antwort zuerst und damit vorbelegt.
      return [
        { action: 'close', label: 'Abbrechen' },
        { action: 'clear', label: 'Alles löschen' },
      ];
    },
    // Zeilen fuer die zweispaltige Anzeige.
    //
    // Primaer steht immer der ECHTE Live-Wert vom Geraet. Weicht der in der DB
    // gespeicherte Wert davon ab, folgt er in Klammern - so ist auf einen Blick
    // sichtbar, wo der Datensatz veraltet ist.
    // Keys ohne Live-Pendant (reine DB-Felder wie buildingId/room, dazu die
    // navigator-Eigenschaften) zeigen unveraendert ihren einzigen Wert.
    infoRows() {
      const head = [
        { key: 'Umgebung', value: this.environment },
        { key: 'Samsung AppID', value: this.appId },
        { key: 'Package ID', value: this.packageId },
        { key: 'App Version', value: process.env.APP_VERSION || '' },
        { key: 'Installierte Apps', value: this.installedApps.length },
      ].filter((r) => r.value !== '' && r.value !== undefined && r.value !== null);

      const deviceRows = Object.keys(this.deviceInfo).map((key) => {
        const live = this.liveValues[key];
        const hasLive = live !== undefined && live !== null && live !== '';
        // Live-Wert gewinnt; ohne Live-Wert bleibt der vorhandene Wert stehen.
        const primary = hasLive ? live : this.deviceInfo[key];
        const db = this.dbValues[key];
        const hasDb = db !== undefined && db !== null && db !== '';
        const differs = hasLive && hasDb && String(db) !== String(live);
        return {
          key,
          value: primary,
          // In Klammern der abweichende DB-Wert (nicht mehr der Live-Wert).
          current: differs ? db : undefined,
          // Zu diesem Feld gibt es einen Live-Wert, aber keinen in der DB.
          // Ohne diese Unterscheidung sieht "DB leer" genauso aus wie
          // "DB identisch" - beides waere einfach keine Klammer.
          dbMissing: hasLive && !hasDb,
          readable: this.formatDate(key, primary),
        };
      });

      return head.concat(deviceRows);
    },
    webosSource() {
      return Diagnostics.webosSource(this.castDiag);
    },
    webosRows() {
      return Diagnostics.webosRows(this.castDiag);
    },
    // Alle Diagnose-Tabellen kommen aus @/helpers/Diagnostics - dieselbe
    // Quelle, aus der die get_info-Antwort an den Socket-Admin gebaut wird.
    // Zwei Implementierungen wuerden auseinanderlaufen, und die Ferndiagnose
    // zeigte dann etwas anderes als dieser Bildschirm.
    castRows() {
      return Diagnostics.castRows({
        diag: this.castDiag,
        softApVerify: this.$store.state.cast.softApVerify,
        langKey: this.$store.getters['lang/getLang'],
      });
    },
    // Speicherbelegung des Geraets (Cookie / LocalStorage / SessionStorage /
    // interner Speicher). Kein Cache: die Seite soll den Stand von JETZT zeigen,
    // und die Abfrage ist billig - vier Speicher durchzaehlen.
    storageSections() {
      return Diagnostics.storageSections();
    },
    softApProbeRows() {
      return Diagnostics.softApProbeRows(this.castDiag);
    },
    castPropertyRows() {
      return Diagnostics.castPropertyRows(this.castDiag);
    },
    castPropertyKnown() {
      return this.castPropertyRows.filter((r) => r.value !== '—').length;
    },
    preloadedAppRows() {
      return Diagnostics.preloadedAppRows(this.castDiag);
    },
    // Wie viele Felder in der DB vom Geraet abweichen - Hinweis darauf, dass ein
    // Device-Update faellig ist (ROT).
    staleCount() {
      return this.infoRows.filter((r) => r.current !== undefined).length;
    },
    missingCount() {
      return this.infoRows.filter((r) => r.dbMissing).length;
    },
    // Aufteilung in zwei moeglichst gleich grosse Spalten.
    columns() {
      const rows = this.infoRows;
      const half = Math.ceil(rows.length / 2);
      return [rows.slice(0, half), rows.slice(half)];
    },
    castApps() {
      return Diagnostics.castApps(this.installedApps);
    },
    deviceSettingRows() {
      return Diagnostics.settingsRows(this.deviceSettings);
    },
    deviceSettingsSupported() {
      const s = this.deviceSettings;
      return (s && s.supported && s.supported.length) || 0;
    },
    deviceSettingsTotal() {
      return DEVICE_SETTINGS.length;
    },
    deviceSettingApiRows() {
      return Diagnostics.settingsApiRows(this.deviceSettings);
    },
    storageProbeRows() {
      return Diagnostics.storageProbeRows(this.storageProbe);
    },
  },
  watch: {},
  mounted() {
    /* eslint-disable */
    console.log('======================================here', navigator, window.navigator);
    console.log("======================================INFO-deviceStat:", this.$store.state.device);
    if(!navigator) return;
    //console.log("=====LENGTH", typeof navigator, Object.keys(navigator).length);
    //this.deviceInfo.width = window.innerWidth;
    //this.deviceInfo.height = window.innerHeight;
    //this.deviceInfo.tizenVersion = Device.tizenVersion;
    //console.log("======================================INFO-deviceStat:", this.$store.state.device);
  },
};
</script>

<style scoped>
.device-info {
  background-color: red;
  height: 100%;
  padding: 1%;
  /* Platz fuer die feste Farbtasten-Leiste, sonst verdeckt sie die letzte
     Tabellenzeile. */
  padding-bottom: 4rem;
  overflow-y: auto;
}
/* Farbtasten-Leiste. Fest am unteren Rand, weil die Seite scrollt. */
.keybar {
  align-items: center;
  background-color: rgba(0, 0, 0, 0.72);
  bottom: 0;
  box-sizing: border-box;
  color: #fff;
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  left: 0;
  padding: 0.5rem 1rem;
  position: fixed;
  width: 100%;
}
.keybar-item {
  align-items: center;
  display: flex;
  white-space: nowrap;
}
/* Obere Legende: dieselbe Aufzaehlung, aber im Textfluss unter der
   Ueberschrift statt fest am Rand. MUSS nach .keybar stehen - gleiche
   Spezifitaet, also gewinnt die spaetere Regel; davor haette position:fixed
   aus .keybar die Legende ebenfalls an den unteren Rand geholt. */
.keybar--top {
  background-color: rgba(0, 0, 0, 0.35);
  border-radius: 0.4rem;
  margin: 0 1rem 0.8rem;
  padding: 0.4rem 0.8rem;
  position: static;
  width: auto;
}
.keybar-title {
  font-weight: bold;
  margin-right: 0.5rem;
}
.dot {
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: 50%;
  display: inline-block;
  height: 0.8em;
  margin-right: 0.4em;
  width: 0.8em;
}
.dot-red {
  background-color: #e03131;
}
.dot-green {
  background-color: #2f9e44;
}
.dot-yellow {
  background-color: #f1c40f;
}
.dot-blue {
  background-color: #1971c2;
}
/* Sicherheitsabfrage. Ueber allem, auch ueber der Farbtasten-Leiste. */
.dialog {
  align-items: center;
  background: rgba(0, 0, 0, 0.75);
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 20;
}
.dialog-box {
  background: #2b2b2b;
  border-radius: 0.8rem;
  box-sizing: border-box;
  color: #fff;
  max-width: 60%;
  padding: 2rem;
  text-align: center;
}
.dialog-title {
  font-size: x-large;
  font-weight: 700;
  margin-bottom: 1rem;
}
.dialog-text {
  line-height: 1.4;
}
.dialog-report {
  color: #b6e0ff;
  margin-top: 1rem;
  word-break: break-word;
}
.dialog-buttons {
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  margin-top: 1.8rem;
}
.dialog-button {
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-radius: 0.4rem;
  padding: 0.5rem 1.5rem;
}
.dialog-button--active {
  background-color: #1971c2;
  border-color: #fff;
}
.key {
  font-weight: bold;
  padding-right: 1rem;
  vertical-align: top;
  width: 40%;
  word-break: break-word;
  overflow-wrap: break-word;
}
.value {
  vertical-align: top;
  width: 60%;
  word-break: break-all;
  overflow-wrap: break-word;
}
.current {
  color: #ffe08a;
  font-style: italic;
}
.legend {
  margin: 0 1rem 0.8rem;
}
.count {
  color: #ffe08a;
}
.missing {
  color: #ffb3b3;
  font-style: italic;
}
.readable {
  color: #b6e0ff;
  font-style: italic;
}
h2 {
  font-size: x-large;
  font-weight: 700;
  margin: 0 1rem 1rem;
}
h3 {
  font-size: large;
  font-weight: 700;
  margin: 1.5rem 1rem 0.5rem;
}
.env-badge {
  display: inline-block;
  margin-left: 1rem;
  padding: 0.1em 0.6em;
  border-radius: 0.4em;
  font-size: medium;
  vertical-align: middle;
  color: #fff;
}
.env-badge.production {
  background-color: #1b7f3b;
}
.env-badge.staging {
  background-color: #c47f00;
}
.info-columns {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  width: 100%;
}
.info-col {
  flex: 1 1 0;
  min-width: 0;
  box-sizing: border-box;
  padding: 0 1rem;
}
.installed-apps {
  padding: 0 1rem;
}
.diagnostics {
  padding: 0 1rem;
}
table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
}
td {
  padding: 0.2rem 0;
}
.info-item {
  border-bottom: 1px solid black;
}
.video-test {
  width: 100vw;
  height: 100vh;
  position: absolute;
  top: 0;
  left: 0;
}
</style>



// WEBPACK FOOTER //
// src/pages/Info.vue